import {message, useIntl} from '@sail/intl';

import {
  // eslint-disable-next-line no-restricted-imports
  Dialog,
  css,
  view,
  Divider,
  Icon,
  DialogContentConfig,
} from '@sail/ui';
import {Button} from '@stripe-internal/connect-embedded-lib';
import React from 'react';
import {cancel} from '@sail/icons/react/Icon';

type BottomDialogProps = {
  handleClose: () => void;
  title?: string;
  footer?: React.ReactNode;
};

const messages = {
  close: message({
    id: 'connectEmbed.BottomDialog.closeButton',
    defaultMessage: 'Close',
    description: 'Button to close the Dialog component',
  }),
};

export const BottomDialog: React.FC<BottomDialogProps> = ({
  handleClose,
  title,
  children,
  footer,
}) => {
  const intl = useIntl();

  return (
    <Dialog
      uses={[
        css({
          position: 'absolute',
          bottom: 'space.0',
          top: 'auto',
          right: 'auto',
          left: 'auto',
          height: 'fit',
          width: 'calc(100% - 2px)',
          borderTopLeftRadius: 'medium',
          borderTopRightRadius: 'medium',
          overflow: 'hidden',
        }),

        // We override the Content config to remove padding as it's otherwise not configurable
        // Some components like Popovers always already have padding so we don't want to add more
        DialogContentConfig.customize({
          css: {padding: 'space.0'},
        }),
      ]}
      onClose={handleClose}
    >
      <view.div
        css={{
          stack: 'y',
        }}
      >
        {/* Header */}
        <view.div
          css={{
            position: 'sticky',
            top: 'space.0',
            backgroundColor: 'surface',
            zIndex: 2,
          }}
        >
          {title && (
            <view.div
              css={{
                stack: 'x',
                gap: 'space.250',
                distribute: 'space-between',
                padding: 'medium',
              }}
            >
              <view.div
                css={{
                  font: 'heading.medium',
                }}
              >
                {title}
              </view.div>
              <Button
                aria-label={intl.formatMessage(messages.close)}
                onPress={handleClose}
                type="icon"
                css={{
                  alignSelfY: 'top',
                }}
              >
                <Icon icon={cancel} size="small" />
              </Button>
            </view.div>
          )}
          <Divider />
        </view.div>

        {/* Content */}
        <view.div
          css={{
            stack: 'y',
            alignX: 'center',
            alignY: 'center',
            width: 'fill',
            gap: 'space.250',
          }}
        >
          {children}
        </view.div>

        {/* Footer */}
        {footer && (
          <>
            <Divider />
            <view.div css={{padding: 'space.250'}}>{footer}</view.div>
          </>
        )}
      </view.div>
    </Dialog>
  );
};
